/**
 * useTaskResolver — given a TaskRecordDto, compute the target route +
 * session-state side-effects so clicking a task from /rtm / Home /
 * Recent Activity navigates the operator to the correct module with
 * context preserved (cifId pinned, cartId resumable, task trail remembered).
 *
 * Examples:
 *   - type: 'AddressVerify'  + cifId    → /rcm/:cifId?step=contact
 *   - type: 'SignatureWait'  + signingSessionId → /rsc/:signingSessionId
 *   - type: 'CartReadyCommit'+ cartId   → /rao/:cartId?step=review
 *   - type: 'FraudAlert'     + cifId    → /rcc?filter=fraud&cifId=:cifId
 *   - fallback → /rtm/:taskId
 *
 * Consumers: Home tile click, Recent Activity row, /rtm task row.
 */
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { TaskRecordDto } from '@retail-ops/shared-types';
import { useSession } from '@/contexts/SessionContext';

export interface ResolvedTaskRoute {
  path: string;
  label: string;
}

/** Pure resolver — exported for unit testing. */
export function resolveTaskRoute(task: TaskRecordDto): ResolvedTaskRoute {
  const t = task.type.toLowerCase();

  // Signing surfaces always win — the session id is specific.
  if (task.signingSessionId && (t.includes('sign') || t.includes('correct'))) {
    return { path: `/rsc/${task.signingSessionId}`, label: 'Open signing session' };
  }

  // Cart-bound tasks → RAO cart detail, deep-link into the relevant step.
  if (task.cartId && (t.includes('commit') || t.includes('review') || t.includes('pipeline'))) {
    return { path: `/rao/${task.cartId}?step=review`, label: 'Open cart' };
  }
  if (task.cartId) {
    return { path: `/rao/${task.cartId}`, label: 'Open cart' };
  }

  // Compliance-categoried tasks → RCC triage with the right filter.
  if (task.category === 'compliance') {
    const q = new URLSearchParams();
    q.set('type', task.type);
    if (task.cifId) q.set('cifId', task.cifId);
    return { path: `/rcc?${q.toString()}`, label: 'Open in Compliance' };
  }

  // CIF-bound tasks → RCM.
  if (task.cifId) {
    if (t.includes('address') || t.includes('contact')) {
      return { path: `/rcm/${task.cifId}?step=contact`, label: 'Open CIF contact' };
    }
    if (t.includes('id') || t.includes('cip')) {
      return { path: `/rcm/${task.cifId}?step=id`, label: 'Open CIF ID' };
    }
    return { path: `/rcm/${task.cifId}`, label: 'Open CIF' };
  }

  // Account-bound fallback — route by accountNumber prefix (legacy heuristic).
  if (task.accountNumber) {
    const acct = task.accountNumber;
    return { path: `/ram?acct=${encodeURIComponent(acct)}`, label: 'Open account' };
  }

  // Ultimate fallback — task detail in RTM.
  return { path: `/rtm/${task.taskId}`, label: 'Open task' };
}

export interface UseTaskResolverResult {
  /** Navigate + pin context in one call. */
  open(task: TaskRecordDto): void;
  /** Pure lookup — useful for rendering the button label. */
  resolve(task: TaskRecordDto): ResolvedTaskRoute;
}

export function useTaskResolver(): UseTaskResolverResult {
  const navigate = useNavigate();
  const session = useSession();

  const open = useCallback((task: TaskRecordDto): void => {
    const route = resolveTaskRoute(task);
    // Pin context so Breadcrumbs + the cart-chip know where we came from.
    session.setLastTask(task.taskId);
    if (task.cifId) session.setLastCif(task.cifId);
    if (task.cartId) session.setActiveCart(task.cartId);
    navigate(route.path);
  }, [navigate, session]);

  const resolve = useCallback((task: TaskRecordDto): ResolvedTaskRoute => resolveTaskRoute(task), []);

  return { open, resolve };
}
